import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'antd';
import ProfileEnrich from 'pages/PPAAdvanceSettingPage/components/ProfileEnrich';
import GoogleSync from 'pages/PPAAdvanceSettingPage/components/GoogleSync';
import { PATH } from 'constants/path';
import { EAIChatPageId } from 'types/ai';
import { getUrlParam } from 'utils/common';
import { getStep } from 'api/ppaOnboarding';
import Layout from '../components/Layout';
import PrefillNotice from '../components/PrefillNotice';
import { EInvitedOnboardingStep } from '../types';
import { EInvitedProviderStep } from '../steps';
import s from './s.module.less';

// https://www.figma.com/design/aGY2cI5da2oGElG6GEd0Wo/Group-Practice?node-id=4914-11335&m=dev
// Advanced settings for invited providers — note: NO "Set up no-show policy" step.

enum EStep {
    INIT = 'init',
    PROFILE_ENRICH = 'profile_enrich',
    GOOGLE_SYNC = 'google_sync',
}

const Steps = [EStep.INIT, EStep.PROFILE_ENRICH, EStep.GOOGLE_SYNC];

const DashedDivider = () => (
    <svg width="1" height="18" viewBox="0 0 1 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <line x1="0.5" y1="0" x2="0.500001" y2="18" stroke="#9CA3AF" strokeDasharray="2 2" />
    </svg>
);

const SolidDivider = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="52" height="2" viewBox="0 0 52 2" fill="none">
        <line y1="1" x2="52" y2="1" stroke="#1F2937" />
    </svg>
);

export default function InvitedProviderAdvancedSettingsPage() {
    const navigate = useNavigate();
    const isValidStep = (val?: string | null): val is EStep => !!val && Object.values(EStep).includes(val as EStep);

    const [step, setStep] = useState<EStep>(() => {
        const tab = getUrlParam('tab');
        return isValidStep(tab) ? tab : EStep.INIT;
    });

    useEffect(() => {
        const tab = getUrlParam('tab');
        if (tab === EStep.GOOGLE_SYNC) {
            setStep(EStep.GOOGLE_SYNC);
        }
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const res = await getStep();
            const saved = res?.data?.data?.currentStep;
            if (cancelled) return;
            if (saved === EInvitedProviderStep.COMPLETED) {
                navigate(PATH.DASHBOARD, { replace: true });
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [navigate]);
    // TODO: derive from backend — true when the admin/owner has pre-filled the invited
    // provider's profile fields.
    const [showPrefillBanner, setShowPrefillBanner] = useState(true);

    const handleNext = () => {
        const nextStep = Steps[Steps.indexOf(step) + 1];
        if (nextStep) {
            setStep(nextStep);
        } else {
            navigate(PATH.INVITED_PROVIDER_ADVANCED_SETTINGS_SUCCESS);
        }
    };

    const handleBack = () => {
        const prevStep = Steps[Steps.indexOf(step) - 1];
        if (prevStep) {
            setStep(prevStep);
        }
    };

    const getAIChatPageId = (): EAIChatPageId => {
        switch (step) {
            case EStep.PROFILE_ENRICH:
                return EAIChatPageId.INVITED_PROVIDER_ADVANCE_SETTING_PROFILE_ENRICH;
            case EStep.GOOGLE_SYNC:
                return EAIChatPageId.INVITED_PROVIDER_ADVANCE_SETTING_GOOGLE_SYNC;
            case EStep.INIT:
            default:
                return EAIChatPageId.INVITED_PROVIDER_ADVANCE_SETTING_INTRO;
        }
    };

    return (
        <Layout
            step={EInvitedOnboardingStep.AVAILABILITY}
            aiChatPageId={getAIChatPageId()}
            showProcessBar={false}
            showPrefillNotice={false}
            naturalScroll
        >
            {step === EStep.INIT && (
                <div className={s.initWrap}>
                    <h2>Set up your advanced settings</h2>
                    <div className={s.desc}>
                        Take a few extra steps to optimize your practice. Completing the required step will ensure your
                        profile runs smoothly, and the optional ones can help you attract more patients and protect your
                        time.
                    </div>
                    <div className={`${s.navWrap} ${s.navWrapInit}`}>
                        <div className={s.navItem}>
                            <div className={s.navInx}>1</div>
                            Enrich marketing profile
                        </div>
                        <DashedDivider />
                        <div className={s.navItem}>
                            <div className={s.navInx}>2</div>
                            Sync Google calendar
                            <div className={s.option}>Optional</div>
                        </div>
                    </div>
                    <div className={s.btnWrap}>
                        <Button type="primary" onClick={handleNext}>
                            Get started
                        </Button>
                    </div>
                </div>
            )}

            {step !== EStep.INIT && (
                <>
                    {step === EStep.PROFILE_ENRICH && showPrefillBanner && (
                        <PrefillNotice onClose={() => setShowPrefillBanner(false)} />
                    )}
                    <div className={s.navWrap}>
                        <div className={`${s.navItem} ${step === EStep.PROFILE_ENRICH ? s.navItemActive : ''}`}>
                            <div className={s.navInx}>1</div>
                            Enrich marketing profile
                        </div>
                        <SolidDivider />
                        <div className={`${s.navItem} ${step === EStep.GOOGLE_SYNC ? s.navItemActive : ''}`}>
                            <div className={s.navInx}>2</div>
                            Sync Google calendar
                            <div className={s.option}>Optional</div>
                        </div>
                    </div>

                    {step === EStep.PROFILE_ENRICH && (
                        <ProfileEnrich
                            onNext={handleNext}
                            onBack={handleBack}
                            stepToSave={EInvitedProviderStep.ADVANCE_SETTING}
                        />
                    )}

                    {step === EStep.GOOGLE_SYNC && (
                        <GoogleSync
                            onNext={handleNext}
                            onBack={handleBack}
                            nextButtonText="Submit"
                            successCallbackPath={`${PATH.INVITED_PROVIDER_ADVANCED_SETTINGS}?tab=${EStep.GOOGLE_SYNC}`}
                        />
                    )}
                </>
            )}
        </Layout>
    );
}
